import type {
  MutableRefObject,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Smallest scale: 1 = image fit to the viewport, never smaller. */
export const ZOOM_MIN_SCALE = 1;
/** Largest scale the user can magnify to. */
export const ZOOM_MAX_SCALE = 4;
/** Scale delta applied per zoom-in / zoom-out activation. */
export const ZOOM_STEP = 0.5;
/** Pixels the image pans per arrow-key press while magnified. */
export const KEYBOARD_PAN_STEP = 48;

type Offset = {
  x: number;
  y: number;
};

type DragOrigin = {
  pointerX: number;
  pointerY: number;
  offsetX: number;
  offsetY: number;
};

/** Imperative + reactive surface returned by {@link useImageZoomPan}. */
export type ImageZoomPan = {
  /** Current zoom scale (>= {@link ZOOM_MIN_SCALE}). */
  scale: number;
  /** Current translation in px, applied after scaling. */
  offset: Offset;
  /** True while the image is magnified beyond fit. */
  isZoomed: boolean;
  /** True while a pointer drag is panning the image. */
  isDragging: boolean;
  /** Whether another zoom-in step is available. */
  canZoomIn: boolean;
  /** Whether another zoom-out step is available. */
  canZoomOut: boolean;
  /** Attach to the element that clips and contains the panned image. */
  containerRef: MutableRefObject<HTMLDivElement | null>;
  /**
   * Attach to the previewed `<img>`. Its intrinsic dimensions drive the
   * pan clamp so the image cannot be dragged into `objectFit: contain`
   * letterbox margins when its aspect ratio differs from the frame.
   */
  imageRef: MutableRefObject<HTMLImageElement | null>;
  /** Increase scale by {@link ZOOM_STEP}, clamped to the max. */
  zoomIn: () => void;
  /** Decrease scale by {@link ZOOM_STEP}, clamped to the min. */
  zoomOut: () => void;
  /** Pan by a pixel delta (keyboard arrows), clamped to the travel bounds. */
  panBy: (deltaX: number, deltaY: number) => void;
  /**
   * Re-clamp the current offset against the latest bounds. Call once the
   * `<img>` loads: a pan performed before the intrinsic size was known
   * used the container box, which can exceed the true rendered-box bounds.
   */
  reclamp: () => void;
  onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
  onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Size of the image as actually painted at scale 1 under
 * `objectFit: contain`. When the intrinsic dimensions are unknown
 * (image not yet loaded) it falls back to the container box, matching
 * the prior fill-the-frame assumption.
 */
const getRenderedImageSize = (
  container: HTMLDivElement,
  image: HTMLImageElement | null
): { width: number; height: number } => {
  const rect = container.getBoundingClientRect();
  const naturalWidth = image?.naturalWidth ?? 0;
  const naturalHeight = image?.naturalHeight ?? 0;
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    rect.width <= 0 ||
    rect.height <= 0
  ) {
    return { width: rect.width, height: rect.height };
  }
  const containScale = Math.min(
    rect.width / naturalWidth,
    rect.height / naturalHeight
  );
  return {
    width: naturalWidth * containScale,
    height: naturalHeight * containScale,
  };
};

/**
 * Keeps the scaled image within its container: the image can travel at
 * most half its overflow (rendered box at scale minus the container) in
 * each axis before an edge would pull into view. Clamping against the
 * rendered (contain-fit) box rather than the container prevents dragging
 * a non-frame-ratio chart into the surrounding letterbox margin.
 */
const clampOffset = (
  offset: Offset,
  scale: number,
  container: HTMLDivElement | null,
  image: HTMLImageElement | null
): Offset => {
  if (container === null || scale <= ZOOM_MIN_SCALE) {
    return { x: 0, y: 0 };
  }
  const rect = container.getBoundingClientRect();
  const rendered = getRenderedImageSize(container, image);
  const maxX = Math.max(0, (rendered.width * scale - rect.width) / 2);
  const maxY = Math.max(0, (rendered.height * scale - rect.height) / 2);
  return {
    x: clamp(offset.x, -maxX, maxX),
    y: clamp(offset.y, -maxY, maxY),
  };
};

/**
 * Headless zoom + drag-to-pan controller for a single image.
 *
 * Error handling and rendering stay in the consuming component; this
 * hook only owns the scale/offset math and pointer bookkeeping.
 */
export const useImageZoomPan = (): ImageZoomPan => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dragOriginRef = useRef<DragOrigin | null>(null);
  const [scale, setScale] = useState(ZOOM_MIN_SCALE);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Re-clamp the pan offset whenever the scale changes (e.g. zooming
  // out shrinks the travel bounds and must pull the image back in).
  useEffect(() => {
    setOffset((current) =>
      clampOffset(current, scale, containerRef.current, imageRef.current)
    );
  }, [scale]);

  const zoomIn = useCallback(() => {
    setScale((prev) => clamp(prev + ZOOM_STEP, ZOOM_MIN_SCALE, ZOOM_MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => clamp(prev - ZOOM_STEP, ZOOM_MIN_SCALE, ZOOM_MAX_SCALE));
  }, []);

  const panBy = useCallback(
    (deltaX: number, deltaY: number) => {
      setOffset((current) =>
        clampOffset(
          { x: current.x + deltaX, y: current.y + deltaY },
          scale,
          containerRef.current,
          imageRef.current
        )
      );
    },
    [scale]
  );

  const reclamp = useCallback(() => {
    setOffset((current) =>
      clampOffset(current, scale, containerRef.current, imageRef.current)
    );
  }, [scale]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (scale <= ZOOM_MIN_SCALE) {
        return;
      }
      dragOriginRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        offsetX: offset.x,
        offsetY: offset.y,
      };
      setIsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [offset.x, offset.y, scale]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const origin = dragOriginRef.current;
      if (origin === null) {
        return;
      }
      const next = {
        x: origin.offsetX + (event.clientX - origin.pointerX),
        y: origin.offsetY + (event.clientY - origin.pointerY),
      };
      setOffset(
        clampOffset(next, scale, containerRef.current, imageRef.current)
      );
    },
    [scale]
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (dragOriginRef.current === null) {
        return;
      }
      dragOriginRef.current = null;
      setIsDragging(false);
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    },
    []
  );

  return {
    scale,
    offset,
    isZoomed: scale > ZOOM_MIN_SCALE,
    isDragging,
    canZoomIn: scale < ZOOM_MAX_SCALE,
    canZoomOut: scale > ZOOM_MIN_SCALE,
    containerRef,
    imageRef,
    zoomIn,
    zoomOut,
    panBy,
    reclamp,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
};
